import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty, authorizedFetch } from './client';
import { keys } from './keys';
import { invalidateAllCommunityAudits } from './communityAudit';
import { sortByDisplayName } from '../lib/sortByName';
const CustomerModelSchema = z.object({
  id: z.coerce.number(),
  customer_id: z.coerce.number(),
  name: z.string(),
  elevation: z.string().nullable().optional(),
  option_name: z.string().nullable().optional(),
  model_code: z.string().nullable().optional(),
  mto_filename: z.string().nullable().optional(),
  mto_line_count: z.coerce.number().optional(),
  mto_unmatched_line_count: z.coerce.number().optional(),
  notes: z.string().nullable().optional(),
  product_type_id: z.coerce.number().nullable().optional(),
  product_type: z.string().nullable().optional(),
  floor_type_id: z.coerce.number().nullable().optional(),
  floor_type: z.string().nullable().optional(),
  series: z.string().nullable().optional(),
  /** phaseId → flat division bid amount */
  division_phase_prices: z.record(z.string(), z.coerce.number()).optional(),
}).passthrough();

export function useCustomerModels(builderId: string | undefined) {
  return useQuery({
    queryKey: keys.customerModels(builderId ?? ''),
    enabled: !!builderId,
    queryFn: async () => {
      const rows = await apiFetch(
        `/api/customers/${builderId}/models`,
        z.array(CustomerModelSchema.passthrough())
      );
      return sortByDisplayName(rows);
    },
  });
}

export function useCatalogModel(modelId: string | undefined) {
  return useQuery({
    queryKey: ['customers', 'models', modelId],
    enabled: !!modelId,
    queryFn: () => apiFetch(`/api/customers/models/${modelId}`, CustomerModelSchema),
  });
}

const MtoLineRowSchema = z.record(z.string(), z.unknown());

export function useCatalogModelLines(modelId: string | undefined) {
  return useQuery({
    queryKey: ['customers', 'models', modelId, 'lines'],
    enabled: !!modelId,
    queryFn: () => apiFetch(`/api/customers/models/${modelId}/mto-lines`, z.array(MtoLineRowSchema)),
  });
}

export type CatalogModelMtoLineInput = {
  qty: number | null;
  item_id: string;
  ref_num: string;
  payable_task_group: string;
};

const PreserveSkippedSchema = z.object({
  itemId: z.string().optional(),
  payableTaskGroup: z.string().optional(),
  reasons: z.array(z.string()).optional(),
});

const PreserveSummarySchema = z.object({
  preservedLineCount: z.coerce.number().optional(),
  skippedLineCount: z.coerce.number().optional(),
  skipped: z.array(PreserveSkippedSchema).optional(),
});

const ReplaceMtoLinesResponseSchema = z.object({
  success: z.boolean(),
  lineCount: z.coerce.number(),
  preserveSummary: PreserveSummarySchema.optional(),
  communityResults: z.array(z.record(z.string(), z.unknown())).optional(),
});

export type CatalogMtoPreserveSummary = z.infer<typeof PreserveSummarySchema>;

export function useReplaceCatalogModelMtoLines(
  modelId: string | undefined,
  builderId: string | undefined
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lines: CatalogModelMtoLineInput[]) => {
      if (!modelId) throw new Error('Model required');
      return apiFetch(
        `/api/customers/models/${modelId}/mto-lines`,
        ReplaceMtoLinesResponseSchema,
        { method: 'PUT', body: JSON.stringify({ lines }) }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', 'models', modelId, 'lines'] });
      void qc.invalidateQueries({ queryKey: ['customers', 'models', modelId] });
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useCatalogModelCommunities(modelId: string | undefined) {
  return useQuery({
    queryKey: ['customers', 'models', modelId, 'communities'],
    enabled: !!modelId,
    queryFn: () =>
      apiFetch(
        `/api/customers/models/${modelId}/communities`,
        z.array(z.record(z.string(), z.unknown()))
      ),
  });
}

export function useCreateCustomerModel(builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      name: string;
      elevation?: string;
      option_name?: string;
      model_code?: string;
      notes?: string;
      product_type_id?: number | null;
      floor_type_id?: number | null;
      series?: string;
      file?: File | null;
    }) => {
      if (!builderId) throw new Error('Builder required');
      const fd = new FormData();
      fd.append('name', vars.name.trim());
      if (vars.elevation?.trim()) fd.append('elevation', vars.elevation.trim());
      if (vars.option_name?.trim()) fd.append('option_name', vars.option_name.trim());
      if (vars.model_code?.trim()) fd.append('model_code', vars.model_code.trim());
      if (vars.notes?.trim()) fd.append('notes', vars.notes.trim());
      if (vars.product_type_id != null) fd.append('product_type_id', String(vars.product_type_id));
      if (vars.floor_type_id != null) fd.append('floor_type_id', String(vars.floor_type_id));
      if (vars.series?.trim()) fd.append('series', vars.series.trim());
      if (vars.file) fd.append('mto_file', vars.file);
      const res = await authorizedFetch(`/api/customers/${builderId}/models`, {
        method: 'POST',
        body: fd,
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        id?: number;
        mtoError?: string;
      };
      if (!res.ok) throw new Error(data.error || 'Create failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
    },
  });
}

export function useUpdateCatalogModel(modelId: string | undefined, builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      notes?: string | null;
      elevation?: string | null;
      option_name?: string | null;
      product_type_id?: number | null;
      floor_type_id?: number | null;
      series?: string | null;
      model_code?: string | null;
      is_active?: boolean;
      division_phase_prices?: Record<string, number>;
    }) => {
      if (!modelId) throw new Error('Model required');
      return apiFetchEmpty(`/api/customers/models/${modelId}`, { method: 'PUT', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', 'models', modelId] });
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
      // Bid payloads include catalog division prices for assigned communities.
      void qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'communities' && q.queryKey[2] === 'bid',
      });
    },
  });
}

export function useDeleteCatalogModel(modelId: string | undefined, builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!modelId) throw new Error('Model required');
      return apiFetchEmpty(`/api/customers/models/${modelId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
    },
  });
}

export function useImportMultiMtoCustomer(builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!builderId) throw new Error('Select a builder');
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch(`/api/customers/${builderId}/import-multi-mto`, {
        method: 'POST',
        body: fd,
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Import failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
      invalidateAllCommunityAudits(qc);
    },
  });
}

/** Pass `builderId` so the builder’s model list refreshes after upload. */
export function useUploadCatalogModelMto(builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { modelId: number; file: File }) => {
      const { modelId, file } = payload;
      const fd = new FormData();
      fd.append('mto_file', file);
      const res = await authorizedFetch(`/api/customers/models/${modelId}/upload-mto`, {
        method: 'POST',
        body: fd,
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        preserveSummary?: CatalogMtoPreserveSummary;
        mtoLineCount?: number;
        communityResults?: unknown[];
      };
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keys.customerModels(builderId ?? '') });
      void qc.invalidateQueries({ queryKey: ['customers', 'models', String(vars.modelId), 'lines'] });
      void qc.invalidateQueries({ queryKey: ['customers', 'models', String(vars.modelId)] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

